/**
 * Utility functions for handling realtime models that natively support audio
 */

// List of model providers that offer realtime models
export const REALTIME_MODEL_PROVIDERS = ['ultravox', 'openai-realtime', 'custom-realtime', 'google-realtime'];

// Google Gemini Live (native audio) models — must stay in sync with
// REALTIME_MODEL_IDS in outbound/llm.py
export const GEMINI_REALTIME_MODEL_IDS = [
  'gemini-3.8-live',
  'gemini-3.1-flash-live-preview',
  'gemini-2.5-flash-native-audio-preview-12-2025',
];

// Prebuilt Gemini Live voices (https://ai.google.dev/gemini-api/docs/live#voices)
export const GEMINI_REALTIME_VOICES = [
  'Achernar', 'Achird', 'Algenib', 'Algieba', 'Alnilam', 'Aoede', 'Autonoe',
  'Callirrhoe', 'Charon', 'Despina', 'Enceladus', 'Erinome', 'Fenrir', 'Gacrux',
  'Iapetus', 'Kore', 'Laomedeia', 'Leda', 'Orus', 'Pulcherrima', 'Puck',
  'Rasalgethi', 'Sadachbia', 'Sadaltager', 'Schedar', 'Sulafat', 'Umbriel',
  'Vindemiatrix', 'Zephyr', 'Zubenelgenubi',
];

// Languages supported by the Gemini Live API
// https://ai.google.dev/gemini-api/docs/live#supported-languages
export const GEMINI_REALTIME_LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-IN', name: 'English (India)' },
  { code: 'hi-IN', name: 'Hindi (India)' },
  { code: 'bn-IN', name: 'Bengali (India)' },
  { code: 'gu-IN', name: 'Gujarati (India)' },
  { code: 'kn-IN', name: 'Kannada (India)' },
  { code: 'ml-IN', name: 'Malayalam (India)' },
  { code: 'mr-IN', name: 'Marathi (India)' },
  { code: 'ta-IN', name: 'Tamil (India)' },
  { code: 'te-IN', name: 'Telugu (India)' },
  { code: 'ar-XA', name: 'Arabic (Generic)' },
  { code: 'de-DE', name: 'German (Germany)' },
  { code: 'es-US', name: 'Spanish (US)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'id-ID', name: 'Indonesian (Indonesia)' },
  { code: 'it-IT', name: 'Italian (Italy)' },
  { code: 'ja-JP', name: 'Japanese (Japan)' },
  { code: 'ko-KR', name: 'Korean (Korea)' },
  { code: 'nl-NL', name: 'Dutch (Netherlands)' },
  { code: 'pl-PL', name: 'Polish (Poland)' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ru-RU', name: 'Russian (Russia)' },
  { code: 'th-TH', name: 'Thai (Thailand)' },
  { code: 'tr-TR', name: 'Turkish (Turkey)' },
  { code: 'vi-VN', name: 'Vietnamese (Vietnam)' },
  { code: 'cmn-CN', name: 'Mandarin Chinese (China)' },
];

export const DEFAULT_GEMINI_REALTIME_VOICE = 'Puck';
export const DEFAULT_GEMINI_REALTIME_LANGUAGE = 'en-US';

// List of specific model IDs that are realtime models
export const REALTIME_MODEL_IDS = [
  // Ultravox models
  'fixie-ai/ultravox',
  'fixie-ai/ultravox-gemma3-27b-preview',
  'fixie-ai/ultravox-llama3.3-70b',
  'fixie-ai/ultravox-qwen3-32b-preview',
  'ultravox',
  'ultravox-v0_2',
  'ultravox-gemma',
  'ultravox-llama',
  'ultravox-qwen',
  // OpenAI realtime models
  'gpt-realtime',
  'gpt-realtime-2.1',
  'gpt-realtime-1.5',
  'gpt-realtime-mini',
  'gpt-realtime-2025-08-28',
  'gpt-4o-realtime-preview',
  'gpt-4o-mini-realtime-preview',
  // Google Gemini Live (native audio) models
  ...GEMINI_REALTIME_MODEL_IDS,
];

// Built-in voices offered by the OpenAI Realtime API (gpt-realtime family)
export const OPENAI_REALTIME_VOICES = [
  { id: 'marin', name: 'Marin' },
  { id: 'cedar', name: 'Cedar' },
  { id: 'alloy', name: 'Alloy' },
  { id: 'ash', name: 'Ash' },
  { id: 'ballad', name: 'Ballad' },
  { id: 'coral', name: 'Coral' },
  { id: 'echo', name: 'Echo' },
  { id: 'sage', name: 'Sage' },
  { id: 'shimmer', name: 'Shimmer' },
  { id: 'verse', name: 'Verse' },
];

export const DEFAULT_OPENAI_REALTIME_VOICE = 'marin';

/**
 * Check if the selected model is a Google Gemini Live (realtime) model
 */
export function isGeminiRealtimeModel(provider?: string, modelId?: string): boolean {
  if (provider && provider.toLowerCase() === 'google-realtime') {
    return true;
  }
  return !!modelId && GEMINI_REALTIME_MODEL_IDS.includes(modelId);
}

/**
 * Check if a model is a realtime model that handles audio natively
 * @param provider Model provider (e.g., 'ultravox')
 * @param modelId Model ID (e.g., 'fixie-ai/ultravox')
 * @returns True if the model is a realtime model
 */
export function isRealtimeModel(provider?: string, modelId?: string): boolean {
  if (provider && REALTIME_MODEL_PROVIDERS.includes(provider.toLowerCase())) {
    return true;
  }
  if (modelId && REALTIME_MODEL_IDS.includes(modelId)) {
    return true;
  }
  return false;
}

/**
 * Get a message explaining realtime model configuration
 * @param provider Model provider
 * @param modelId Model ID
 * @returns Explanation message or null if not a realtime model
 */
export function getRealtimeModelMessage(provider?: string, modelId?: string): string | null {
  if (isGeminiRealtimeModel(provider, modelId)) {
    return 'Gemini Live is a realtime speech-to-speech model that handles audio natively. Voice settings (TTS/STT) will be disabled — use the Realtime Voice and Realtime Language fields below instead.';
  }
  if (isRealtimeModel(provider, modelId)) {
    return 'This is a realtime model that handles both speech-to-text and text-to-speech natively. Voice settings (TTS/STT) will be disabled.';
  }
  return null;
}